import fetch from "isomorphic-fetch";
import { message, setCurrentShow, setHistoryRoute, setIsFetching, setRoute, setTitleAndScrollToTop, setUserGuest } from "../actions";
import Configuration, { translations } from "../config";
import Access from "../components/Access";
import { getOrderableQuestionnaires, getQuestionnairesOverview, setQuestionnaires, setQuestionnairesOverview } from "../plugins/questionnaires/actions/Questionnaires";
import { setOrganizationsOverview } from "../plugins/questionnaires/actions/Organizations";
import { setPermissionsOverview, setRolesOverview } from "./Roles";
import ReactGA from "react-ga";
import { resetBlocks, setNextSurveyQuestion, setSurveyAnswers, setSurveys } from "../plugins/questionnaires/actions/Surveys";
import { setQuestionnaireDocuments } from "../plugins/questionnaires/actions/Documents";
import { setDomains } from "../plugins/reports/actions/Domains";

/**
 * Konstanten
 */
export const SET_USER = "SET_USER";
export const SET_USERS_OVERVIEW = "SET_USERS_OVERVIEW";
export const SET_USERS = "SET_USERS";
export const SET_USER_COUNT = "SET_USER_COUNT";
export const SET_ARTICLES = "SET_ARTICLES";
export const SET_FIELD_OPTIONS = "SET_FIELD_OPTIONS";
export const SET_ORGANIZATION_LIMIT = "SET_ORGANIZATION_LIMIT";
export const SET_SURVEY_COSTS = "SET_SURVEY_COSTS";
export const SET_BASIC_CHARGE = "SET_BASIC_CHARGE";

/**
 * Setter
 */
export function setUser( json, jwt = null )
{
	return {
		type: SET_USER,
		json,
		jwt
	};
}

export function setUsersOverview( json )
{
	return {
		type: SET_USERS_OVERVIEW,
		json
	};
}

export function setUsers( json )
{
	return {
		type: SET_USERS,
		json
	};
}

export function setUserCount( json )
{
	return {
		type: SET_USER_COUNT,
		json
	};
}

export function setArticles( json )
{
	return {
		type: SET_ARTICLES,
		json
	};
}

export function setFieldOptions( json )
{
	return {
		type: SET_FIELD_OPTIONS,
		json
	};
}

export function setOrganizationLimit( json )
{
	return {
		type: SET_ORGANIZATION_LIMIT,
		json
	};
}

export function setSurveyCosts( json )
{
	return {
		type: SET_SURVEY_COSTS,
		json
	};
}

export function setBasicCharge( json )
{
	return {
		type: SET_BASIC_CHARGE,
		json
	};
}

/**
 * Nutzt die Api um die Daten des eingeloggten Nutzers im Store zu speichern
 */
export function fetchUser()
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		const jwt = JSON.parse( localStorage.getItem( "jwt" ) );
		const current = new Date();
		
		if ( jwt && current.getTime() < jwt.expires )
		{
			// Google Analytics
			if ( localStorage.getItem( "ga" ) )
			{
				ReactGA.event( {
					category: "User",
					action: "Benutzer überprüfen"
				} );
			}
			
			return fetch(
				`${Configuration.apiUrl}/whoami`,
				{
					credentials: "include",
					method: "GET",
					headers: { "JWT": jwt.value }
				}
			)
				.then(
					response =>
					{
						if ( response.ok )
						{
							response.json()
								.then( user =>
									{
										const jwt = response.headers.get( "jwt" );
										
										dispatch( setUser( user, jwt ) );
										
										// Nach erfolgreichem Login benötigen wir auf der Startseite alle Fragbögen für die Auswahl
										if ( Configuration.pluginName === "questionnaires" && Access.isPermitted( user, [ "select_questionnaires" ] ) )
										{
											dispatch( getQuestionnairesOverview() );
										}
										
										let d = new Date();
										d.setTime( d.getTime() + ( 10 * 60 * 1000 ) );
										
										localStorage.setItem( "jwt", JSON.stringify( { value: jwt, path: "/", expires: d.getTime() } ) );
										
										// Google Analytics
										if ( localStorage.getItem( "ga" ) )
										{
											ReactGA.set( { userId: user.id } );
											ReactGA.event( {
												category: "User",
												action: "Benutzer angemeldet mit ID",
												value: user.id,
												nonInteraction: true
											} );
										}
									}
								);
						}
						else
						{
							// setze Gastnutzer Erkennung
							dispatch( setUserGuest( false ) );
							
							// Auf der Startseite alle Fragbögen als Artikel laden
							Configuration.pluginName === "questionnaires" && dispatch( getOrderableQuestionnaires() );
							
							// Google Analytics
							if ( localStorage.getItem( "ga" ) )
							{
								ReactGA.set( { userId: 0 } );
							}
						}
						
						dispatch( setIsFetching( false ) );
					}
				)
				.catch(
					error =>
					{
						dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
						dispatch( setIsFetching( false ) );
						
						// setze Gastnutzer Erkennung
						dispatch( setUserGuest( false ) );
						
						// Auf der Startseite alle Fragbögen als Artikel laden
						Configuration.pluginName === "questionnaires" && dispatch( getOrderableQuestionnaires() );
						
						// Google Analytics
						if ( localStorage.getItem( "ga" ) )
						{
							ReactGA.exception( {
								description: "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.",
								fatal: true,
								nonInteraction: true
							} );
						}
					}
				);
		}
		else
		{
			// setze Gastnutzer Erkennung
			dispatch( setUserGuest( false ) );
			
			// Auf der Startseite alle Fragbögen als Artikel laden
			Configuration.pluginName === "questionnaires" && dispatch( getOrderableQuestionnaires() );
			
			dispatch( setIsFetching( false ) );
		}
	};
	
} // function fetchUser()

/**
 * Nutzt die Api um die Daten des eingeloggten Nutzers im Store zu speichern
 */
export function loginUser( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzeranmeldung"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/login`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( user =>
								{
									const jwt = response.headers.get( "jwt" );
									
									dispatch( setUser( user, jwt ) );
									
									// Nach erfolgreichem Login benötigen wir auf der Startseite alle Fragbögen für die Auswahl
									if ( Configuration.pluginName === "questionnaires" && Access.isPermitted( user, [ "select_questionnaires" ] ) )
									{
										dispatch( getQuestionnairesOverview() );
									}
									
									let d = new Date();
									d.setTime( d.getTime() + ( 10 * 60 * 1000 ) );
									
									localStorage.setItem( "jwt", JSON.stringify( { value: jwt, path: "/", expires: d.getTime() } ) );
									
									// Umleitung setzen
									switch ( getState().historyRoute )
									{
										case "/passwort":
										case "/passwort-vergessen":
											dispatch( setRoute( "/" ) );
											dispatch( setHistoryRoute( null ) );
											break;
										default:
											// dispatch( setRoute( getState().historyRoute ) );
											dispatch( setRoute( "/" ) );
									}
									
									// Google Analytics
									if ( localStorage.getItem( "ga" ) )
									{
										ReactGA.set( { userId: user.id } );
									}
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
						
						// Google Analytics
						if ( localStorage.getItem( "ga" ) )
						{
							ReactGA.set( { userId: 0 } );
						}
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function loginUser(...)

/**
 * Nutzt die Api um die Daten des eingeloggten Nutzers zu resetten
 */
export function logoutUser()
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzerabmeldung"
			} );
		}
		
		if ( Configuration.pluginName === "reports" )
		{
			dispatch( setDomains( [] ) );
		}
		else if ( Configuration.pluginName === "questionnaires" )
		{
			dispatch( getOrderableQuestionnaires() );
			dispatch( setQuestionnaires( [] ) );
			dispatch( setQuestionnairesOverview( [] ) );
			dispatch( setSurveys( [] ) );
			dispatch( resetBlocks( [] ) );
			dispatch( setSurveyAnswers( [] ) );
			dispatch( setNextSurveyQuestion( null ) );
			dispatch( setQuestionnaireDocuments( [] ) );
			
		}
		
		dispatch( setUserGuest() );
		dispatch( setUsersOverview( [] ) );
		dispatch( setRolesOverview( [] ) );
		dispatch( setPermissionsOverview( [] ) );
		dispatch( setOrganizationsOverview( [] ) );
		dispatch( setCurrentShow( null ) );
		dispatch( setUserCount( null ) );
		// dispatch( setTitleAndScrollToTop( null ) );
		
		localStorage.removeItem( "jwt" );
		
		dispatch( setIsFetching( false ) );
	};
	
} // function logoutUser(...)

/**
 * Nutzt die Api um den Benutzer zu registrieren.
 */
export function registrateUser( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzer registrieren"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/registration`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					return response.json()
						.then( json =>
							{
								dispatch( setIsFetching( false ) );
								if ( response.ok )
								{
									return json;
								}
								else
								{
									dispatch( message( json.error, "error" ) );
									return 0;
								}
							}
						);
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function registrateUser(...)

/**
 * Nutzt die Api um die E-Mail und Organisation zu prüfen.
 */
export function checkUserAndOrganization( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Überprüfung auf doppelte E-Mail oder Organisation"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/user-organization-validation`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// alles ist Okay
						dispatch( setIsFetching( false ) );
						return true;
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "info" ) );
								}
							);
						dispatch( setIsFetching( false ) );
						return false;
					}
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
					return false;
				}
			);
	};
	
} // function checkUserAndOrganization(...)

/**
 * Nutzt die Api um den IBAN zu prüfen.
 */
export function checkIBAN( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Überprüfung der IBAN"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/iban-validation`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// alles ist Okay
						dispatch( setIsFetching( false ) );
						return true;
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
						dispatch( setIsFetching( false ) );
						return false;
					}
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
					return false;
				}
			);
	};
	
} // function checkIBAN(...)

/**
 * Nutzt die Api um einen Benutzer zu aktivieren.
 */
export function activateUser( a_key )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzer aktivieren"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/activation`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( { key: a_key } )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									dispatch( message( "Benutzer aktiviert.", "info", 5000 ) );
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
									
								}
							);
					}
					
					dispatch( setRoute( "/" ) );
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function activateUser(...)

/**
 * Nutzt die Api um das Benutzerpasswort zu ändern.
 */
export function changeUserPassword( a_formData, a_user_id )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Passwort ändern"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers[ "content-type" ] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/users/${a_user_id}/password`,
			{
				credentials: "include",
				method: "POST",
				body: JSON.stringify( a_formData ),
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// Benutzer aktivieren
						dispatch( message( "Ihr Passwort wurde geändert.", "info", 5000 ) );
						dispatch( setRoute( "/" ) );
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function changeUserPassword(...)

/**
 * Nutzt die Api um das Benutzerpasswort zu ändern.
 */
export function resetUserPassword( a_formData, key )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Passwort vergessen"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/password-reset`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// Benutzer aktivieren
						dispatch( message( "Ihr Passwort wurde geändert.", "info", 5000 ) );
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
					dispatch( setRoute( "/" ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function changeUserPassword(...)

/**
 * Nutzt die Api um das Benutzerpasswort zu ändern.
 */
export function addUserPassword( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Passwort anlegen"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/password`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// Benutzer aktivieren
						dispatch( activateUser( a_formData.key ) );
						dispatch( message( "Ihr Passwort wurde geändert.", "info", 5000 ) );
						dispatch( setRoute( "/" ) );
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function addUserPassword(...)

/**
 * Nutzt die Api um zu Prüfen ob das Benutzerpasswort geändert werden darf.
 */
export function checkAddUserPassword( a_token )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Erlaubnisprüfung - Passwort anlegen"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/validate-set-password`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( { key: a_token } )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						// alles ist Okay
						dispatch( setIsFetching( false ) );
						return true;
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "info" ) );
								}
							);
						dispatch( setIsFetching( false ) );
						return false;
					}
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function checkAddUserPassword(...)

/**
 * Nutzt die Api um eine E-Mail zum Passwort zurücksetzen zu senden.
 */
export function getResetMail( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "E-Mail Passwort vergessen"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/password-reset-mail`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( a_formData )
			}
		)
			.then(
				response =>
				{
					// Anfragen mit ungültiger E-Mail werden ignoriert
					dispatch( message( "Ihnen wurde eine E-Mail zum Ändern des Passwortes zugeschickt.", "info", 5000 ) );
					dispatch( setRoute( "/" ) );
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setRoute( "/" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function getResetMail(...)

/**
 * Nutzt die Api um
 */
export function validateToken( token )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Token Validierung"
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/validate-token`,
			{
				credentials: "include",
				method: "POST",
				headers: { "content-type": "application/json" },
				body: JSON.stringify( { key: token } )
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						dispatch( setTitleAndScrollToTop( "Passwort zurücksetzen" ) );
					}
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
									dispatch( setRoute( "/" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function validateToken(...)

/**
 * Nutzt die Api um die Daten aller Benutzer im Store zu
 */
export function getUsersOverview()
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzerliste"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/users?${+new Date()}`,
			{
				credentials: "include",
				method: "GET",
				headers
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									dispatch( setUsersOverview( json.users ) );
									dispatch( setOrganizationsOverview( json.organizations ) );
									dispatch( setRolesOverview( json.roles ) );
									dispatch( setUserCount( json.userCount ) );
									dispatch( setPermissionsOverview( {} ) );
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
		
	};
	
} // function getUsersOverview()

/**
 * Nutzt die Api um die Daten eines neuen Benutzers anzulegen und im Store zu speichern
 *
 * @param a_formData {Object}
 */
export function createUser( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzer anlegen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers[ "content-type" ] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/users`,
			{
				credentials: "include",
				method: "POST",
				body: JSON.stringify( a_formData ),
				headers
			}
		)
			.then(
				response =>
				{
					// Benutzer angelegt
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									// Benutzer in dem State erweitern und Benutzerzähler aktualisieren
									const users = Object.assign(
										{},
										getState().users,
										{ [ Object.keys( getState().users ).length ]: json }
									);
									
									dispatch( setUsersOverview( users ) );
									
									// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
									dispatch( message( "Benutzer erfolgreich angelegt.", "info", 5000 ) );
								}
							);
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function postUser()

/**
 * Nutzt die Api um die Daten des Benutzers zu ändern und im Store zu speichern
 *
 * @param a_formData {Object}
 */
export function patchUser( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzerdaten ändern"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers[ "content-type" ] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/users/${a_formData.id}`,
			{
				credentials: "include",
				method: "PATCH",
				body: JSON.stringify( a_formData ),
				headers
			}
		)
			.then(
				response =>
				{
					// Benutzer geändert
					if ( response.ok )
					{
						// Benutzer aktualisieren
						dispatch( setUsers( a_formData ) );
						
						// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
						dispatch( message( "Benutzer erfolgreich geändert.", "info", 5000 ) );
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function patchUser()

/**
 * Nutzt die Api um die Daten des eingeloggten Nutzers im Store zu speichern
 *
 * @param a_formData {Object}
 */
export function deleteUser( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Benutzer löschen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		
		return fetch(
			`${Configuration.apiUrl}/users/${a_formData.id}`,
			{
				credentials: "include",
				method: "DELETE",
				headers
			}
		)
			.then(
				response =>
				{
					// Benutzer gelöscht
					if ( response.ok )
					{
						// Benutzer aus dem State entfernen und Benutzerzähler aktualisieren
						const users = Object.keys( getState().users )
							.filter( a_key => getState().users[ a_key ].id !== a_formData.id )
							.map( a_key => getState().users[ a_key ] );
						
						dispatch( setUsersOverview( users ) );
						
						// Der Nutzer soll ein Alert erhalten, dass die Anfrage erfolgreich war.
						dispatch( message( "Benutzer erfolgreich gelöscht.", "info", 5000 ) );
						
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function deleteUser()

/**
 * Gibt alle Artikel zurück.
 */
export function getCurrentArticles()
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: `Aktuelle ${translations.de_DE.words.articles} laden`
			} );
		}
		
		return fetch(
			`${Configuration.apiUrl}/orders/articles?${+new Date()}`,
			{
				credentials: "include",
				method: "GET"
			}
		)
			.then(
				response =>
				{
					if ( response.ok )
					{
						response.json()
							.then( json =>
								{
									dispatch( setArticles( json.articles ) );
									dispatch( setFieldOptions( json.fieldOptions ) );
								}
							);
					}
					else
					{
						response.json()
							.then( json =>
								{
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function getCurrentArticles(...)

/**
 * Nutzt die Api um ein Monatliches Limit anzulegen und im Store zu speichern
 *
 * @param a_formData {Object}
 */
export function setMonthlyLimit( a_formData )
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Monatliches Preislimit setzen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers[ "content-type" ] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/users/organization_limit`,
			{
				credentials: "include",
				method: "POST",
				body: JSON.stringify( a_formData ),
				headers
			}
		)
			.then(
				response =>
				{
					// Benutzer angelegt
					if ( response.ok )
					{
						dispatch( setOrganizationLimit( a_formData.monthly_limit ) );
						dispatch( message( "Kostenlimit erfolgreich gesetzt.", "info", 5000 ) );
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function setMonthlyLimit()

/**
 * Nutzt die Api um ein Monatliches Limit anzulegen und im Store zu speichern
 *
 */
export function resetMonthlyLimit()
{
	return function( dispatch, getState )
	{
		dispatch( setIsFetching( true ) );
		
		// Google Analytics
		if ( localStorage.getItem( "ga" ) )
		{
			ReactGA.event( {
				category: "User",
				action: "Monatliches Preislimit setzen"
			} );
		}
		
		const jwt = getState().jwt;
		let headers = jwt !== null ? { "JWT": jwt } : {};
		headers[ "content-type" ] = "application/json";
		
		return fetch(
			`${Configuration.apiUrl}/users/organization_limit`,
			{
				credentials: "include",
				method: "PATCH",
				headers
			}
		)
			.then(
				response =>
				{
					// Benutzer angelegt
					if ( response.ok )
					{
						dispatch( setOrganizationLimit( false ) );
						dispatch( message( "Kostenlimit erfolgreich zurückgesetzt.", "info", 5000 ) );
					}
					// Eingabefehler oder Serverfehler
					else
					{
						response.json()
							.then( json =>
								{
									// Der Nutzer erfährt warum die Anfrage fehlgeschlagen ist.
									dispatch( message( json.error, "error" ) );
								}
							);
					}
					dispatch( setIsFetching( false ) );
				}
			)
			.catch(
				error =>
				{
					dispatch( message( "Der Dienst steht derzeit nicht zur Verfügung. Bitte versuchen Sie es später erneut.", "error" ) );
					dispatch( setIsFetching( false ) );
				}
			);
	};
	
} // function setMonthlyLimit()